import React from 'react';
import CharacterActionEconomyPanel from './CharacterActionEconomyPanel';
import './CharacterActionEconomyEditor.css';

const ACTION_BUCKETS = Object.freeze([
  { key: 'actions', label: 'Actions', activationType: 'action' },
  { key: 'bonusActions', label: 'Bonus Actions', activationType: 'bonus action' },
  { key: 'reactions', label: 'Reactions', activationType: 'reaction' }
]);

const toActionObject = (entry) => typeof entry === 'string'
  ? { name: entry, description: '' }
  : { ...(entry || {}) };

/**
 * Reads the canonical nested action economy while accepting the editor's already-loaded
 * top-level arrays without modifying the source character.
 * date_of_change: 2026-08-08
 * rule_reference: D&D 5e actions, Bonus Actions, and Reactions are distinct timing buckets.
 */
export const readRecordedActionEconomy = (character) => {
  const nested = character?.actions && typeof character.actions === 'object' && !Array.isArray(character.actions)
    ? character.actions
    : {};
  return {
    actions: (Array.isArray(nested.actions)
      ? nested.actions
      : (Array.isArray(character?.actions) ? character.actions : [])).map(toActionObject),
    bonusActions: (Array.isArray(nested.bonusActions)
      ? nested.bonusActions
      : (Array.isArray(character?.bonusActions) ? character.bonusActions : [])).map(toActionObject),
    reactions: (Array.isArray(nested.reactions)
      ? nested.reactions
      : (Array.isArray(character?.reactions) ? character.reactions : [])).map(toActionObject)
  };
};

const writeRecordedActionEconomy = (character, economy) => {
  const nested = character?.actions && typeof character.actions === 'object' && !Array.isArray(character.actions)
    ? character.actions
    : {};
  const next = {
    ...character,
    actions: {
      ...nested,
      actions: economy.actions,
      bonusActions: economy.bonusActions,
      reactions: economy.reactions
    }
  };

  // Keep an already-present top-level editing shape synchronized so hydration cannot
  // reintroduce a stale deleted entry. date_of_change: 2026-08-08.
  if (Array.isArray(character?.bonusActions)) next.bonusActions = economy.bonusActions;
  if (Array.isArray(character?.reactions)) next.reactions = economy.reactions;
  return next;
};

const normalizeDamage = (entry) => {
  if (Array.isArray(entry?.damage)) {
    return entry.damage.map((damage) => typeof damage === 'string'
      ? { formula: damage, type: '' }
      : {
          ...damage,
          formula: String(damage?.formula || damage?.dice || damage?.roll || ''),
          type: String(damage?.type || damage?.damageType || '')
        });
  }
  if (typeof entry?.damage === 'string' && entry.damage.trim()) {
    return [{ formula: entry.damage.trim(), type: String(entry.damageType || '') }];
  }
  return [];
};

const asNonNegativeNumber = (value) => Math.max(0, parseInt(value, 10) || 0);

const formatRange = (range) => {
  if (typeof range === 'string' || typeof range === 'number') return String(range);
  if (!range || typeof range !== 'object' || Array.isArray(range)) return '';
  const normal = Number(range.normal);
  const long = Number(range.long);
  if (Number.isFinite(normal) && Number.isFinite(long) && long > normal) return `${normal}/${long} ft.`;
  if (Number.isFinite(normal)) return `${normal} ft.`;
  if (Number.isFinite(Number(range.reach))) return `${Number(range.reach)} ft. reach`;
  return '';
};

const CharacterActionEconomyEditor = ({ character, setCharacter, viewModel }) => {
  const economy = readRecordedActionEconomy(character);

  const commit = (nextEconomy) => setCharacter((previous) => (
    previous ? writeRecordedActionEconomy(previous, nextEconomy) : previous
  ));

  const patchEntry = (bucket, index, patch) => {
    const nextEconomy = readRecordedActionEconomy(character);
    nextEconomy[bucket] = nextEconomy[bucket].map((entry, entryIndex) => entryIndex === index
      ? { ...entry, ...patch }
      : entry);
    commit(nextEconomy);
  };

  const moveEntry = (bucket, index, targetBucket) => {
    if (bucket === targetBucket) return;
    const nextEconomy = readRecordedActionEconomy(character);
    const [entry] = nextEconomy[bucket].splice(index, 1);
    const targetDefinition = ACTION_BUCKETS.find((definition) => definition.key === targetBucket);
    nextEconomy[targetBucket].push({
      ...entry,
      activationType: targetDefinition?.activationType || 'action',
      actionType: targetDefinition?.activationType || 'action'
    });
    commit(nextEconomy);
  };

  const addEntry = (bucket) => {
    const nextEconomy = readRecordedActionEconomy(character);
    const definition = ACTION_BUCKETS.find((entry) => entry.key === bucket);
    nextEconomy[bucket].push({
      name: `New ${definition?.label?.replace(/s$/, '') || 'Action'}`,
      category: 'custom',
      type: bucket === 'reactions' ? 'reaction' : (bucket === 'bonusActions' ? 'bonus' : 'action'),
      activationType: definition?.activationType || 'action',
      actionType: definition?.activationType || 'action',
      description: '',
      damage: []
    });
    commit(nextEconomy);
  };

  const removeEntry = (bucket, index) => {
    const nextEconomy = readRecordedActionEconomy(character);
    nextEconomy[bucket] = nextEconomy[bucket].filter((_, entryIndex) => entryIndex !== index);
    commit(nextEconomy);
  };

  const patchDamage = (bucket, index, damageIndex, patch) => {
    const entry = economy[bucket][index];
    const damage = normalizeDamage(entry).map((item, itemIndex) => itemIndex === damageIndex
      ? { ...item, ...patch }
      : item);
    patchEntry(bucket, index, { damage });
  };

  const addDamage = (bucket, index) => {
    const entry = economy[bucket][index];
    patchEntry(bucket, index, { damage: [...normalizeDamage(entry), { formula: '', type: '' }] });
  };

  const removeDamage = (bucket, index, damageIndex) => {
    const entry = economy[bucket][index];
    patchEntry(bucket, index, {
      damage: normalizeDamage(entry).filter((_, itemIndex) => itemIndex !== damageIndex)
    });
  };

  return (
    <div className="character-action-editor">
      <section className="character-action-editor__recorded" aria-labelledby="recorded-combat-options-title">
        <header className="character-action-editor__header">
          <div>
            <h3 id="recorded-combat-options-title">Recorded Combat Options</h3>
            <p>Add class, feat, item, and custom options. Weapons and spells are edited in their own sections.</p>
          </div>
          <div className="character-action-editor__add-actions">
            {ACTION_BUCKETS.map((definition) => (
              <button key={definition.key} type="button" onClick={() => addEntry(definition.key)}>
                Add {definition.label.replace(/s$/, '')}
              </button>
            ))}
          </div>
        </header>

        {ACTION_BUCKETS.map((definition) => (
          <section className="character-action-editor__bucket" key={definition.key}>
            <h4>{definition.label}</h4>
            {economy[definition.key].length === 0 ? (
              <p className="character-action-editor__empty">No recorded {definition.label.toLowerCase()}.</p>
            ) : (
              <div className="character-action-editor__entries">
                {economy[definition.key].map((entry, index) => {
                  const entryName = String(entry.name || entry.title || 'Unnamed option');
                  const damageEntries = normalizeDamage(entry);
                  const uses = entry.uses && typeof entry.uses === 'object' ? entry.uses : {};
                  const savingThrow = entry.savingThrow && typeof entry.savingThrow === 'object' ? entry.savingThrow : {};
                  return (
                    <details className="character-action-editor__entry" key={`${definition.key}-${entry.id || index}`}>
                      <summary>
                        <span>{entryName}</span>
                        <small>{definition.label.replace(/s$/, '')}</small>
                      </summary>
                      <div className="character-action-editor__fields">
                        <label className="character-action-editor__field--wide">
                          <span>Name</span>
                          <input
                            type="text"
                            value={entryName === 'Unnamed option' ? '' : entryName}
                            aria-label={`${definition.label} ${index + 1} name`}
                            onChange={(event) => patchEntry(definition.key, index, { name: event.target.value })}
                          />
                        </label>
                        <label>
                          <span>Timing</span>
                          <select
                            value={definition.key}
                            aria-label={`${entryName} timing`}
                            onChange={(event) => moveEntry(definition.key, index, event.target.value)}
                          >
                            {ACTION_BUCKETS.map((bucket) => <option key={bucket.key} value={bucket.key}>{bucket.label.replace(/s$/, '')}</option>)}
                          </select>
                        </label>
                        <label>
                          <span>Category</span>
                          <input
                            type="text"
                            value={entry.category || ''}
                            placeholder="class feature"
                            aria-label={`${entryName} category`}
                            onChange={(event) => patchEntry(definition.key, index, { category: event.target.value })}
                          />
                        </label>
                        <label>
                          <span>Type</span>
                          <select
                            value={entry.type || 'action'}
                            aria-label={`${entryName} type`}
                            onChange={(event) => patchEntry(definition.key, index, { type: event.target.value })}
                          >
                            {['action', 'attack', 'weapon', 'bonus', 'reaction', 'free', 'object', 'item'].map((type) => (
                              <option key={type} value={type}>{type.replace(/^./, (letter) => letter.toUpperCase())}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Ability</span>
                          <select
                            value={entry.ability || ''}
                            aria-label={`${entryName} ability`}
                            onChange={(event) => patchEntry(definition.key, index, { ability: event.target.value })}
                          >
                            <option value="">None</option>
                            {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map((ability) => (
                              <option key={ability} value={ability}>{ability.replace(/^./, (letter) => letter.toUpperCase())}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>To Hit</span>
                          <input
                            type="number"
                            value={entry.toHit ?? entry.attackBonus ?? ''}
                            placeholder="+7"
                            aria-label={`${entryName} to hit`}
                            onChange={(event) => patchEntry(definition.key, index, {
                              toHit: event.target.value === '' ? undefined : parseInt(event.target.value, 10),
                              attackBonus: event.target.value === '' ? undefined : parseInt(event.target.value, 10)
                            })}
                          />
                        </label>
                        <label>
                          <span>Range</span>
                          <input
                            type="text"
                            value={formatRange(entry.range)}
                            placeholder="30 ft."
                            aria-label={`${entryName} range`}
                            onChange={(event) => patchEntry(definition.key, index, { range: event.target.value })}
                          />
                        </label>
                        <label>
                          <span>Reach</span>
                          <input
                            type="text"
                            value={entry.reach || ''}
                            placeholder="5 ft."
                            aria-label={`${entryName} reach`}
                            onChange={(event) => patchEntry(definition.key, index, { reach: event.target.value })}
                          />
                        </label>
                        <label>
                          <span>Save</span>
                          <select
                            value={savingThrow.ability || ''}
                            aria-label={`${entryName} saving throw ability`}
                            onChange={(event) => patchEntry(definition.key, index, {
                              savingThrow: { ...savingThrow, ability: event.target.value }
                            })}
                          >
                            <option value="">None</option>
                            {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map((ability) => (
                              <option key={ability} value={ability}>{ability.slice(0, 3).toUpperCase()}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Save DC</span>
                          <input
                            type="number"
                            min="0"
                            value={savingThrow.dc ?? ''}
                            aria-label={`${entryName} saving throw DC`}
                            onChange={(event) => patchEntry(definition.key, index, {
                              savingThrow: { ...savingThrow, dc: event.target.value === '' ? undefined : asNonNegativeNumber(event.target.value) }
                            })}
                          />
                        </label>
                        <label>
                          <span>Max Uses</span>
                          <input
                            type="number"
                            min="0"
                            value={uses.max ?? ''}
                            aria-label={`${entryName} maximum uses`}
                            onChange={(event) => patchEntry(definition.key, index, {
                              uses: { ...uses, max: event.target.value === '' ? undefined : asNonNegativeNumber(event.target.value) }
                            })}
                          />
                        </label>
                        <label>
                          <span>Uses Left</span>
                          <input
                            type="number"
                            min="0"
                            value={uses.current ?? ''}
                            aria-label={`${entryName} uses remaining`}
                            onChange={(event) => patchEntry(definition.key, index, {
                              uses: { ...uses, current: event.target.value === '' ? undefined : asNonNegativeNumber(event.target.value) }
                            })}
                          />
                        </label>
                        <label>
                          <span>Recharge</span>
                          <select
                            value={uses.resetOn || ''}
                            aria-label={`${entryName} recharge`}
                            onChange={(event) => patchEntry(definition.key, index, { uses: { ...uses, resetOn: event.target.value } })}
                          >
                            <option value="">None</option>
                            <option value="short rest">Short Rest</option>
                            <option value="long rest">Long Rest</option>
                            <option value="day">Daily</option>
                          </select>
                        </label>
                        {definition.key === 'reactions' && (
                          <label className="character-action-editor__field--wide">
                            <span>Trigger</span>
                            <input
                              type="text"
                              value={entry.trigger || ''}
                              aria-label={`${entryName} trigger`}
                              onChange={(event) => patchEntry(definition.key, index, { trigger: event.target.value })}
                            />
                          </label>
                        )}
                      </div>

                      <div className="character-action-editor__damage">
                        <div className="character-action-editor__subheading">
                          <strong>Damage</strong>
                          <button type="button" onClick={() => addDamage(definition.key, index)}>Add damage</button>
                        </div>
                        {damageEntries.length === 0 ? (
                          <p>No damage recorded.</p>
                        ) : damageEntries.map((damage, damageIndex) => (
                          <div className="character-action-editor__damage-row" key={`damage-${damageIndex}`}>
                            <label>
                              <span>Formula</span>
                              <input
                                type="text"
                                value={damage.formula}
                                placeholder="1d8+4"
                                aria-label={`${entryName} damage ${damageIndex + 1} formula`}
                                onChange={(event) => patchDamage(definition.key, index, damageIndex, { formula: event.target.value })}
                              />
                            </label>
                            <label>
                              <span>Type</span>
                              <input
                                type="text"
                                value={damage.type}
                                placeholder="piercing"
                                aria-label={`${entryName} damage ${damageIndex + 1} type`}
                                onChange={(event) => patchDamage(definition.key, index, damageIndex, { type: event.target.value })}
                              />
                            </label>
                            <button type="button" onClick={() => removeDamage(definition.key, index, damageIndex)}>Remove</button>
                          </div>
                        ))}
                      </div>

                      <label className="character-action-editor__description">
                        <span>Rules & Notes</span>
                        <textarea
                          rows={4}
                          value={entry.description || entry.desc || entry.notes || ''}
                          aria-label={`${entryName} rules and notes`}
                          onChange={(event) => patchEntry(definition.key, index, { description: event.target.value })}
                        />
                      </label>
                      <button
                        type="button"
                        className="character-action-editor__remove"
                        aria-label={`Remove ${entryName}`}
                        onClick={() => removeEntry(definition.key, index)}
                      >
                        Remove {definition.label.replace(/s$/, '')}
                      </button>
                    </details>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </section>

      <section className="character-action-editor__reference" aria-labelledby="derived-action-reference-title">
        <header>
          <h3 id="derived-action-reference-title">Derived Play Reference</h3>
          <p>Updates automatically from your recorded options, weapons, spells, and movement.</p>
        </header>
        <CharacterActionEconomyPanel viewModel={viewModel} showTitle={false} compact />
      </section>
    </div>
  );
};

export default CharacterActionEconomyEditor;
